import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { EMBRYO_POSITION } from '@/constants';
import { IMPLANT_TYPE } from '@/pages/embryo-app/components/EmbryoForm/constants';
import { useAccessToken } from '@/services/auth';
import { onFarmImplantQueueLookupOptions } from '@/services/on-farm-implant';

export type QueueFieldOption<T extends string | number> = {
  value: T;
  label: string;
};

const RETIRED_IMPLANT_TYPE_PREFIX = 'DO NOT USE';

export const IMPLANT_QUEUE_IMPLANT_TYPES: QueueFieldOption<number>[] =
  Object.entries(IMPLANT_TYPE)
    .filter(([label]) => !label.startsWith(RETIRED_IMPLANT_TYPE_PREFIX))
    .map(([label, value]) => ({ value, label }));

export const useImplantQueueFieldOptions = () => {
  const { t } = useTranslation();

  const token = useAccessToken();
  const { data } = useQuery(onFarmImplantQueueLookupOptions(token));

  return useMemo(() => {
    const techOptions: QueueFieldOption<string>[] = (
      data?.implantTechs ?? []
    ).map((option) => ({ value: option.id, label: option.name }));

    const farmLocationOptions: QueueFieldOption<string>[] = (
      data?.farmLocations ?? []
    ).map((option) => ({ value: option.id, label: option.name }));

    const clSideOptions: QueueFieldOption<number>[] = [
      {
        value: EMBRYO_POSITION.LEFT,
        label: t('onFarmImplant.implantQueue.clSides.left'),
      },
      {
        value: EMBRYO_POSITION.RIGHT,
        label: t('onFarmImplant.implantQueue.clSides.right'),
      },
    ];

    return {
      techOptions,
      farmLocationOptions,
      implantTypeOptions: IMPLANT_QUEUE_IMPLANT_TYPES,
      clSideOptions,
    };
  }, [data, t]);
};
